import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSelectModule } from '@angular/material/select'
import { MatTableModule } from '@angular/material/table'
import { HeaderBarComponent } from '@components/header-bar/header-bar.component'
import { IntegrationSidebarComponent } from '@components/integration-platform/integration-sidebar/integration-sidebar.component'
import { IntegrationLogEntry } from '@core/models/integration-logs.model'
import { IntegrationLogsService } from '@core/services/integration-logs.service'

@Component({
  selector: 'app-integration-logs',
  templateUrl: './integration-logs.component.html',
  styleUrls: ['./integration-logs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatProgressSpinnerModule,
    HeaderBarComponent,
    IntegrationSidebarComponent,
  ],
})
export class IntegrationLogsComponent {
  private readonly integrationLogsService = inject(IntegrationLogsService)
  private readonly defaultFilter = 'all'
  private readonly defaultLinesNumber = 200

  isLoading$ = signal<boolean>(false)
  errorMessage$ = signal<string>('')

  logs$ = signal<IntegrationLogEntry[]>([])
  searchTerm$ = signal<string>('')
  selectedFilter$ = signal<string>(this.defaultFilter)
  linesNumber$ = signal<number>(this.defaultLinesNumber)

  displayedColumns: string[] = ['logLine', 'timestamp', 'level', 'logger', 'message']
  readonly availableFilters = ['all', 'debug', 'info', 'warning', 'error', 'critical']

  displayedLogs$ = computed(() => {
    const logs = this.logs$()
    const selectedFilter = this.selectedFilter$().toLowerCase()
    const search = this.searchTerm$().trim().toLowerCase()

    const logsByLevel =
      selectedFilter === 'all'
        ? logs
        : logs.filter((log) => (log.level || '').toLowerCase() === selectedFilter)

    if (!search) {
      return logsByLevel
    }

    return logsByLevel.filter((log) => {
      const logContent =
        `${log.log_line} ${log.timestamp} ${log.level} ${log.logger} ${log.message}`.toLowerCase()
      return logContent.includes(search)
    })
  })

  constructor() {
    this.refreshLogs()
  }

  async refreshLogs() {
    this.isLoading$.set(true)
    this.errorMessage$.set('')

    try {
      const response = await this.integrationLogsService.getLogs(
        this.linesNumber$(),
        this.selectedFilter$(),
      )

      if (!response.success) {
        this.logs$.set([])
        this.errorMessage$.set(
          response.error || 'Unable to retrieve logs from the integration server.',
        )
        return
      }

      this.logs$.set((response.logs || []).slice().reverse())
    } finally {
      this.isLoading$.set(false)
    }
  }

  async clearFilters() {
    this.searchTerm$.set('')
    this.selectedFilter$.set(this.defaultFilter)
    this.linesNumber$.set(this.defaultLinesNumber)
    await this.refreshLogs()
  }

  async onFilterChange(filter: string) {
    this.selectedFilter$.set(filter)
    await this.refreshLogs()
  }

  setLinesNumber(linesNumber: number) {
    if (!Number.isFinite(linesNumber)) {
      this.linesNumber$.set(this.defaultLinesNumber)
      return
    }

    this.linesNumber$.set(Math.max(1, Math.min(10000, Math.floor(linesNumber))))
  }

  formatTimestamp(timestamp: string): string {
    if (!timestamp) {
      return '-'
    }

    const [datePart, timePart] = timestamp.split('T')
    if (!datePart || !timePart) {
      return timestamp
    }

    const [year, month, day] = datePart.split('-')
    const time = timePart.split('.')[0]
    if (!year || !month || !day || !time) {
      return timestamp
    }

    return `${day}/${month}/${year} ${time}Z`
  }
}
